import { phdScannerApiAxios } from '../lib/axios-interceptor';

export interface UniversityOpportunity {
  id: string;
  title: string;
  description: string;
  university: string;
  city: string;
  country: string;
  type: string;
  department: string;
  opportunity_url: string;
  logo_url?: string;
  closing_date: string;
  open_to: string;
  status: 'pending' | 'active' | 'inactive';
  supervisor: string;
  supervisor_url: string;
  university_url: string;
  fully_funded: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateOpportunityRequest {
  title: string;
  description: string;
  university: string;
  city: string;
  country: string;
  type: string;
  department: string;
  opportunity_url?: string;
  closing_date?: string;
  open_to?: string;
  supervisor: string;
  supervisor_url?: string;
  university_url?: string;
  fully_funded: boolean;
}

export interface UpdateOpportunityRequest extends Partial<CreateOpportunityRequest> {
  id: string;
}

export interface OpportunityResponse {
  success: boolean;
  message: string;
  opportunity?: UniversityOpportunity;
  opportunities?: UniversityOpportunity[];
}

export const UniversityOpportunityService = {
  async createOpportunity(data: CreateOpportunityRequest): Promise<OpportunityResponse> {
    try {
      const response = await phdScannerApiAxios.post('/api/university/opportunities', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create opportunity');
    }
  },

  async getOpportunities(): Promise<UniversityOpportunity[]> {
    try {
      const response = await phdScannerApiAxios.get('/api/university/opportunities');
      return response.data.opportunities || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch opportunities');
    }
  },

  async getOpportunityById(id: string): Promise<UniversityOpportunity> {
    try {
      const response = await phdScannerApiAxios.get(`/api/university/opportunities/${id}`);
      return response.data.opportunity;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch opportunity');
    }
  },

  async updateOpportunity(data: UpdateOpportunityRequest): Promise<OpportunityResponse> {
    try {
      const { id, ...updateData } = data;
      const response = await phdScannerApiAxios.put(`/api/university/opportunities/${id}`, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update opportunity');
    }
  },

  async deleteOpportunity(id: string): Promise<OpportunityResponse> {
    try {
      const response = await phdScannerApiAxios.delete(`/api/university/opportunities/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete opportunity');
    }
  },

  async updateOpportunityStatus(id: string, status: 'pending' | 'active' | 'inactive'): Promise<OpportunityResponse> {
    try {
      const response = await phdScannerApiAxios.patch(`/api/university/opportunities/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update opportunity status');
    }
  },

  async getOpportunityStats(): Promise<{
    total: number;
    active: number;
    pending: number;
    inactive: number;
  }> {
    try {
      const response = await phdScannerApiAxios.get('/api/university/opportunities/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch opportunity statistics');
    }
  }
};
